import { getVisibleBounds } from "./data";
import { PRICEFRAME } from "./priceFrame";
import { plotHeight } from "./state";
import { clamp } from "./math";
import { priceToY } from "./transformation";
import type { PriceLabel, PriceTick, State } from "./types";

const NICE_MANTISSAS = [1, 1.5, 2, 2.5, 3, 4, 5, 7.5] as const;
const PRICE_PADDING_GRIDS = 2;

export function getPriceConfig(state: State) {
  return PRICEFRAME[state.timeframe];
}

export function getLatestPricePrecision(state: State): number {
  const latestTick = state.chartData[state.chartData.length - 1];
  const precision = latestTick?.precision ?? latestTick?.pip_size ?? 2;
  if (!Number.isFinite(precision)) return 2;
  return Math.max(0, Math.min(8, Math.trunc(precision)));
}

export function getAutoPricePadding(state: State, dataRange: number): number {
  if (!state.tightPriceScale) {
    return Math.max(dataRange * 0.15, 0.05);
  }

  const precision = getLatestPricePrecision(state);
  const priceStep = Math.pow(10, -precision);
  const rangePadding = Math.max(dataRange * 0.03, 0);
  const precisionPadding = priceStep / 2;
  return Math.max(rangePadding, precisionPadding);
}

export function getMinimumAutoPriceRange(state: State): number {
  if (!state.tightPriceScale) return 0.2;
  return Math.max(Math.pow(10, -getLatestPricePrecision(state)), 0.00001);
}

export function clampPriceRange(state: State, min: number, max: number): { min: number; max: number } {
  const config = getPriceConfig(state);
  let range = max - min;
  range = clamp(range, config.minRange, config.maxRange);
  min = Math.max(0.01, min);
  return { min, max: min + range };
}

export function pickNicePriceStep(
  priceRange: number,
  plotHeightPx: number,
  targetPx: number = 100,
  minPx: number = 50,
  maxPx: number = 160,
  prevStep?: number,
  hysteresisPx: number = 10,
): number {
  if (priceRange <= 0 || plotHeightPx <= 0) return 1;

  // Cap the price range to avoid extreme zoom out
  const maxAllowedRange = 100; // Maximum price range to display nicely
  const effectiveRange = Math.min(priceRange, maxAllowedRange);

  const rawStep = (effectiveRange / plotHeightPx) * targetPx;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));

  let bestStep = mag;
  let bestScore = Infinity;

  for (const m of NICE_MANTISSAS) {
    for (const scale of [mag / 10, mag, mag * 10]) {
      const step = m * scale;
      const spacingPx = (step / effectiveRange) * plotHeightPx;

      const zoomingOutToThisStep = prevStep !== undefined && step > prevStep;
      const effectiveMin = zoomingOutToThisStep ? minPx + hysteresisPx : minPx;

      if (spacingPx < effectiveMin) continue;

      const inBand = spacingPx <= maxPx;
      const dist = Math.abs(spacingPx - targetPx);
      const score = inBand ? dist : dist + 1000;

      if (score < bestScore) {
        bestScore = score;
        bestStep = step;
      }
    }
  }

  return bestStep;
}

function computeVisiblePriceRange(state: State): { priceMin: number; priceMax: number } | null {
  const bounds = getVisibleBounds(state.chartData, state.timeStart, state.timeEnd);
  if (!bounds) return null;

  let lo = Infinity;
  let hi = -Infinity;

  for (let i = bounds.from; i < bounds.to; i++) {
    const p = state.chartData[i];
    if (p.quote < lo) lo = p.quote;
    if (p.quote > hi) hi = p.quote;
  }

  if (!isFinite(lo) || !isFinite(hi)) return null;

  if (lo === hi) {
    const pad = state.tightPriceScale
      ? getMinimumAutoPriceRange(state) / 2
      : lo * 0.01 || 1;
    return { priceMin: lo - pad, priceMax: hi + pad };
  }

  const dataRange = hi - lo;
  const midpoint = (hi + lo) / 2;
  const step = pickNicePriceStep(dataRange, plotHeight(state), 60, 36, 120);
  const pad = PRICE_PADDING_GRIDS * step;

  const rawMin = midpoint - dataRange / 2 - pad;
  const rawMax = midpoint + dataRange / 2 + pad;

  return {
    priceMin: Math.floor(rawMin / step) * step,
    priceMax: Math.ceil(rawMax / step) * step,
  };
}

export function getPriceStep(state: State, range: number): number {
  return pickNicePriceStep(range, plotHeight(state), 60, 36, 120);
}

export function snapPrice(value: number, step: number): number {
  return Math.round(value / step) * step;
}

export function fitPriceRangeInstant(state: State): void {
  const range = computeVisiblePriceRange(state);
  if (!range) return;

  let minPrice = range.priceMin;
  let maxPrice = range.priceMax;

  // Keep the view tight, but always leave a little room so candles and labels
  // do not sit directly on the axis edges.
  const dataRange = maxPrice - minPrice;
  const padding = getAutoPricePadding(state, dataRange);

  let finalMin = minPrice - padding;
  let finalMax = maxPrice + padding;

  let finalRange = finalMax - finalMin;
  const minimumRange = getMinimumAutoPriceRange(state);
  if (finalRange < minimumRange) {
    const center = (finalMin + finalMax) / 2;
    finalMin = center - minimumRange / 2;
    finalMax = center + minimumRange / 2;
  }

  const maxAllowedRange = Math.max(dataRange * 2, 20);
  if (finalMax - finalMin > maxAllowedRange) {
    const mid = (finalMin + finalMax) / 2;
    const halfRange = maxAllowedRange / 2;
    finalMin = mid - halfRange;
    finalMax = mid + halfRange;
  }

  state.priceMin = Math.max(0.01, finalMin);
  state.priceMax = Math.max(
    state.priceMin + (state.tightPriceScale ? minimumRange : 0.01),
    finalMax,
  );
}

export function updatePriceRangeFromData(state: State): void {
  fitPriceRangeInstant(state);
}

export function buildPriceAxis(state: State): { step: number; labelEvery: number; ticks: PriceTick[]; labels: PriceLabel[] } {
  const range = state.priceMax - state.priceMin;
  const precision = getLatestPricePrecision(state);
  const precisionStep = Math.pow(10, -precision);
  const step = state.tightPriceScale
    ? Math.max(getPriceStep(state, range), precisionStep)
    : getPriceStep(state, range);
  const firstTick = Math.ceil(state.priceMin / step) * step;
  const ticks: PriceTick[] = [];
  const labels: PriceLabel[] = [];
  const pixelsPerTick = step * (plotHeight(state) / range);
  const minLabelSpacing = 30;
  const labelEvery = Math.max(1, Math.ceil(minLabelSpacing / Math.max(1, pixelsPerTick)));
  const edgePaddingPx = Math.min(42, Math.max(18, pixelsPerTick * 0.45));
  const plotTop = state.top;
  const plotBottom = state.top + plotHeight(state);

  const tickCount = Math.floor((state.priceMax - firstTick) / step + 1e-9);
  for (let index = 0; index <= tickCount; index++) {
    const p = firstTick + index * step;
    const y = priceToY(state, p);
    if (y > plotTop + edgePaddingPx && y < plotBottom - edgePaddingPx) {
      const tickNumber = index;
      ticks.push({ value: p, y, tickNumber });
      if (tickNumber % labelEvery === 0) {
        labels.push({ value: p, y, label: p.toFixed(precision) });
      }
    }
  }

  return { step, labelEvery, ticks, labels };
}

export function generatePriceLabels(state: State): PriceLabel[] {
  return buildPriceAxis(state).labels;
}

export function validateAndFixPriceRange(state: State): void {
  if (!state.chartData || state.chartData.length === 0) return;

  const bounds = getVisibleBounds(state.chartData, state.timeStart, state.timeEnd);
  if (!bounds) return;

  let dataMin = Infinity;
  let dataMax = -Infinity;
  for (let i = bounds.from; i < bounds.to; i++) {
    const point = state.chartData[i];
    dataMin = Math.min(dataMin, point.quote);
    dataMax = Math.max(dataMax, point.quote);
  }

  if (!Number.isFinite(dataMin) || !Number.isFinite(dataMax)) return;

  if (state.priceMin > dataMin || state.priceMax < dataMax) {
    fitPriceRangeInstant(state);
  }

  const currentRange = state.priceMax - state.priceMin;
  if (currentRange < (state.tightPriceScale ? getMinimumAutoPriceRange(state) : 0.01)) {
    if (state.tightPriceScale) {
      fitPriceRangeInstant(state);
      return;
    }
    state.priceMin = dataMin - 0.5;
    state.priceMax = dataMax + 0.5;
  }
}
